/**
 * Flash Messages Component
 * Renders flash messages with animations and proper styling
 */

import React from 'react';
import {
  Alert,
  AlertTitle,
  Slide,
  Box,
  IconButton,
  Stack,
} from '@mui/material';
import {
  Close as CloseIcon,
  Info as InfoIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
} from '@mui/icons-material';
import { useFlashMessagesContext } from '../contexts/FlashMessagesContext';
import { FlashMessage } from '../types/flashMessages';

// Message type to MUI Alert severity mapping
const getSeverity = (type: FlashMessage['type']): 'info' | 'success' | 'warning' | 'error' => {
  return type;
};

// Message type to icon mapping
const getIcon = (type: FlashMessage['type']) => {
  switch (type) {
    case 'success':
      return <CheckCircleIcon />;
    case 'warning':
      return <WarningIcon />;
    case 'error':
      return <ErrorIcon />;
    case 'info':
    default:
      return <InfoIcon />;
  }
};

interface FlashMessageItemProps {
  message: FlashMessage;
  onDismiss: (id: string) => void;
}

const FlashMessageItem: React.FC<FlashMessageItemProps> = ({ message, onDismiss }) => {
  const handleClose = () => {
    onDismiss(message.id);
  };

  return (
    <Slide direction="left" in={true} mountOnEnter unmountOnExit>
      <Alert
        severity={getSeverity(message.type)}
        icon={getIcon(message.type)}
        sx={{
          width: '100%',
          borderRadius: 2,
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
          '& .MuiAlert-message': {
            flexGrow: 1,
          },
        }}
        action={
          message.dismissible ? (
            <IconButton
              aria-label="close"
              color="inherit"
              size="small"
              onClick={handleClose}
              sx={{
                alignSelf: 'flex-start',
                marginTop: '-2px',
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          ) : null
        }
      >
        {message.title && (
          <AlertTitle sx={{ fontWeight: 600, mb: message.message ? 0.5 : 0 }}>
            {message.title}
          </AlertTitle>
        )}
        {message.message && (
          <Box sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>
            {message.message}
          </Box>
        )}
      </Alert>
    </Slide>
  );
};

const FlashMessages: React.FC = () => {
  const { messages, removeMessage } = useFlashMessagesContext();

  if (messages.length === 0) {
    return null;
  }

  return (
    <Box
      sx={{
        position: 'fixed',
        bottom: 16,
        right: 16,
        zIndex: (theme) => theme.zIndex.snackbar,
        pointerEvents: 'none', // Allow clicks to pass through empty areas
        maxWidth: 400,
        width: { xs: 'calc(100vw - 32px)', sm: 'auto' }, // Full width on mobile minus margins
      }}
    >
      <Stack spacing={1} direction="column-reverse" sx={{ pointerEvents: 'auto' }}>
        {messages.map((message) => (
          <FlashMessageItem
            key={message.id}
            message={message}
            onDismiss={removeMessage}
          />
        ))}
      </Stack>
    </Box>
  );
};

export default FlashMessages;
